/**
 * LandingPage - Professional landing page explaining Erasmus+ and our service
 * SEO optimized, institutional European tone, converts high-value clients
 */

import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  Grid,
  Card,
  AppBar,
  Toolbar,
  useTheme,
  useMediaQuery
} from '@mui/material';
import TermsOfService from './TermsOfService';
import PrivacyPolicy from './PrivacyPolicy';
import CookiePolicy from './CookiePolicy';
import CookieBanner from './CookieBanner';
import {
  AutoAwesome as AIIcon,
  School as SchoolIcon,
  Public as PublicIcon,
  Groups as GroupsIcon,
  AccountBalance as InstitutionIcon,
  Lightbulb as IdeaIcon,
  Handshake as PartnershipIcon,
  CheckCircle as CheckIcon,
  ArrowForward as ArrowIcon,
  Speed as SpeedIcon,
  Description as DocIcon,
  Psychology as PsychologyIcon,
  Verified as VerifiedIcon,
  Timeline as TimelineIcon,
  CloudDone as CloudIcon,
  Login as LoginIcon,
  ArrowBack as BackIcon,
  LinkedIn as LinkedInIcon
} from '@mui/icons-material';

interface LandingPageProps {
  onStartProject: () => void;
  onShowLogin: () => void;
  onBack: () => void;
  onShowContact?: () => void;
  onShowProjects?: () => void;
  onShowInsights?: () => void;
  isHomepage?: boolean;
}

const LandingPage: React.FC<LandingPageProps> = ({ onStartProject, onShowLogin, onBack, onShowContact, onShowProjects, onShowInsights, isHomepage = false }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [showTerms, setShowTerms] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showCookiePolicy, setShowCookiePolicy] = useState(false);

  // Show Terms of Service page
  if (showTerms) {
    return <TermsOfService onBack={() => setShowTerms(false)} />;
  }

  // Show Privacy Policy page
  if (showPrivacy) {
    return <PrivacyPolicy onBack={() => setShowPrivacy(false)} />;
  }

  // Show Cookie Policy page
  if (showCookiePolicy) {
    return <CookiePolicy onBack={() => setShowCookiePolicy(false)} />;
  }

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      bgcolor: '#f8f9fa',
      fontFamily: '"Inter", "Segoe UI", sans-serif'
    }}>
      {/* Navigation */}
      <AppBar 
        position="sticky" 
        elevation={0}
        sx={{ 
          bgcolor: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          borderBottom: '1px solid rgba(0, 51, 153, 0.1)'
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between', py: 2, minHeight: 80 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {!isHomepage && (
              <Button
                startIcon={<BackIcon />}
                onClick={onBack}
                sx={{ 
                  color: '#003399',
                  fontWeight: 600,
                  fontSize: '1rem',
                  py: 1
                }}
              >
                Back
              </Button>
            )}
            <Box
              component="img"
              src="/logo.png"
              alt="Erasmus+ Generator"
              sx={{ height: 56, objectFit: 'contain' }}
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
            {isHomepage && onShowProjects && (
              <Button
                onClick={onShowProjects}
                sx={{
                  color: '#003399',
                  fontWeight: 700,
                  fontSize: '1.1rem',
                  py: 1,
                  px: 2,
                  '&:hover': { bgcolor: 'rgba(0, 51, 153, 0.05)' }
                }}
              >
                Projects
              </Button>
            )}
            {isHomepage && onShowInsights && (
              <Button
                onClick={onShowInsights}
                sx={{
                  color: '#003399',
                  fontWeight: 700,
                  fontSize: '1.1rem',
                  py: 1,
                  px: 2,
                  '&:hover': { bgcolor: 'rgba(0, 51, 153, 0.05)' }
                }}
              >
                Insights
              </Button>
            )}
            <Button
              variant="outlined"
              onClick={onShowLogin}
              sx={{
                borderColor: '#003399',
                color: '#003399',
                fontWeight: 700,
                fontSize: '1.1rem',
                px: 4,
                py: 1.2,
                borderWidth: 2,
                '&:hover': { bgcolor: 'rgba(0, 51, 153, 0.05)', borderWidth: 2 }
              }}
            >
              Sign In
            </Button>
            <Button
              variant="contained"
              onClick={onStartProject}
              sx={{
                bgcolor: '#003399',
                fontWeight: 700,
                fontSize: '1.1rem',
                px: 4,
                py: 1.2,
                '&:hover': { bgcolor: '#002266' }
              }}
            >
              Start Now
            </Button>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #001a4d 0%, #003399 40%, #0052cc 100%)',
          position: 'relative',
          overflow: 'hidden',
          py: { xs: 8, md: 12 }
        }}
      >
        {/* Abstract European network pattern */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: 0.1,
            backgroundImage: `
              radial-gradient(circle at 20% 30%, #FFCC00 2px, transparent 2px),
              radial-gradient(circle at 80% 20%, #FFCC00 2px, transparent 2px),
              radial-gradient(circle at 40% 70%, #FFCC00 2px, transparent 2px),
              radial-gradient(circle at 60% 50%, #FFCC00 3px, transparent 3px),
              radial-gradient(circle at 10% 60%, #FFCC00 2px, transparent 2px),
              radial-gradient(circle at 90% 80%, #FFCC00 2px, transparent 2px),
              radial-gradient(circle at 30% 90%, #FFCC00 2px, transparent 2px),
              radial-gradient(circle at 70% 85%, #FFCC00 2px, transparent 2px)
            `,
            backgroundSize: '100% 100%'
          }}
        />
        
        {/* Connection lines simulation */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: 0.05,
            background: `
              linear-gradient(45deg, transparent 40%, rgba(255,204,0,0.3) 50%, transparent 60%),
              linear-gradient(-45deg, transparent 40%, rgba(255,204,0,0.2) 50%, transparent 60%),
              linear-gradient(90deg, transparent 45%, rgba(255,204,0,0.2) 50%, transparent 55%)
            `
          }}
        />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={7}>
              <Typography
                component="h1"
                sx={{
                  fontSize: { xs: '2.2rem', md: '3.2rem' },
                  fontWeight: 800,
                  color: 'white',
                  lineHeight: 1.2,
                  mb: 3,
                  letterSpacing: '-0.02em'
                }}
              >
                Erasmus+ Project Generator
                <Box component="span" sx={{ display: 'block', color: '#FFCC00' }}>
                  for European Organisations
                </Box>
              </Typography>
              
              <Typography
                variant="h6"
                sx={{
                  color: 'rgba(255, 255, 255, 0.9)',
                  mb: 4,
                  fontWeight: 400,
                  lineHeight: 1.7,
                  maxWidth: 600
                }}
              >
                Erasmus Plus is one of the largest European public funding programmes.
                We help organisations design, structure, and prepare projects ready for official submission.
              </Typography>

              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  size="large"
                  endIcon={<ArrowIcon />}
                  onClick={onStartProject}
                  sx={{
                    bgcolor: '#FFCC00',
                    color: '#003399',
                    fontWeight: 700,
                    px: 4,
                    py: 1.5,
                    fontSize: '1.1rem',
                    '&:hover': { bgcolor: '#e6b800', transform: 'translateY(-2px)' },
                    transition: 'all 0.2s'
                  }}
                >
                  Generate Your Project
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  startIcon={<DocIcon />}
                  onClick={() => window.open('/ai-erasmus-guide-marinuzzi.pdf', '_blank')}
                  sx={{
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                    color: 'white',
                    fontWeight: 600,
                    px: 4,
                    py: 1.5,
                    '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' }
                  }}
                >
                  Download Guide
                </Button>
              </Box>
            </Grid>

            <Grid item xs={12} md={5}>
              {/* Budget highlight card */}
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  background: 'rgba(255, 255, 255, 0.08)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(255, 204, 0, 0.3)',
                  borderRadius: 4,
                  textAlign: 'center'
                }}
              >
                <Typography
                  sx={{
                    fontSize: { xs: '3.5rem', md: '4.5rem' },
                    fontWeight: 800,
                    color: '#FFCC00',
                    lineHeight: 1,
                    mb: 1
                  }}
                >
                  26
                </Typography>
                <Typography
                  sx={{
                    fontSize: '2rem',
                    fontWeight: 700,
                    color: '#FFCC00',
                    mb: 2
                  }}
                >
                  BILLION
                </Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.9)', fontWeight: 500 }}>
                  Total Programme Budget
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', mt: 1 }}>
                  2021-2027 Programming Period
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Who Erasmus+ Supports Section */}
      <Box sx={{ py: { xs: 8, md: 10 }, bgcolor: 'white' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography
              variant="overline"
              sx={{ 
                color: '#FFCC00', 
                fontWeight: 700, 
                letterSpacing: 2,
                bgcolor: 'rgba(255, 204, 0, 0.1)',
                px: 2,
                py: 0.5,
                borderRadius: 1,
                display: 'inline-block',
                mb: 2
              }}
            >
              ELIGIBILITY
            </Typography>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 700,
                color: '#003399',
                mb: 3
              }}
            >
              Who Erasmus Plus Supports
            </Typography>
            <Typography
              variant="h6"
              sx={{ color: '#666', maxWidth: 800, mx: 'auto', fontWeight: 400 }}
            >
              Erasmus Plus funds projects developed by a wide range of organisations across Europe
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {[
              { icon: <SchoolIcon />, title: 'Universities', desc: 'Higher education institutions' },
              { icon: <InstitutionIcon />, title: 'Schools', desc: 'Training centres and academies' },
              { icon: <GroupsIcon />, title: 'NGOs', desc: 'Foundations and associations' },
              { icon: <PublicIcon />, title: 'Public Bodies', desc: 'And private organisations' }
            ].map((item, idx) => (
              <Grid item xs={6} md={3} key={idx}>
                <Card 
                  elevation={0}
                  sx={{ 
                    height: '100%', 
                    textAlign: 'center', 
                    p: 3,
                    border: '1px solid #e0e0e0',
                    borderRadius: 3,
                    transition: 'all 0.3s',
                    '&:hover': {
                      borderColor: '#003399',
                      transform: 'translateY(-4px)',
                      boxShadow: '0 12px 40px rgba(0, 51, 153, 0.12)'
                    }
                  }}
                >
                  <Box
                    sx={{
                      width: 64,
                      height: 64,
                      borderRadius: '50%',
                      bgcolor: 'rgba(0, 51, 153, 0.08)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: 2,
                      '& svg': { fontSize: 32, color: '#003399' }
                    }}
                  >
                    {item.icon}
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#003399', mb: 1 }}>
                    {item.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {item.desc}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Improvement areas */}
          <Box sx={{ mt: 8, textAlign: 'center' }}>
            <Typography variant="h6" sx={{ color: '#003399', fontWeight: 600, mb: 4 }}>
              To design, test and scale projects that improve:
            </Typography>
            <Grid container spacing={2} justifyContent="center">
              {[
                'Skills and competences',
                'Innovation and digital transition',
                'International cooperation',
                'Social impact and inclusion',
                'Organisational growth'
              ].map((area, idx) => (
                <Grid item key={idx}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      bgcolor: 'rgba(0, 51, 153, 0.05)',
                      px: 3,
                      py: 1.5,
                      borderRadius: 2,
                      border: '1px solid rgba(0, 51, 153, 0.1)'
                    }}
                  >
                    <CheckIcon sx={{ color: '#FFCC00', fontSize: 20 }} />
                    <Typography sx={{ fontWeight: 500, color: '#003399' }}>{area}</Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>

            <Paper
              elevation={0}
              sx={{
                mt: 6,
                p: 4,
                bgcolor: 'rgba(255, 204, 0, 0.08)',
                border: '2px solid #FFCC00',
                borderRadius: 3,
                maxWidth: 600,
                mx: 'auto'
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#003399' }}>
                You do not need to be a large university.
              </Typography>
              <Typography variant="h6" sx={{ color: '#003399', mt: 1 }}>
                You need a solid project and a European vision.
              </Typography>
            </Paper>
          </Box>
        </Container>
      </Box>

      {/* Our Role Section */}
      <Box 
        sx={{ 
          py: { xs: 8, md: 10 }, 
          background: 'linear-gradient(180deg, #f8f9fa 0%, #eef1f5 100%)'
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography
                variant="overline"
                sx={{ 
                  color: '#003399', 
                  fontWeight: 700, 
                  letterSpacing: 2
                }}
              >
                OUR ROLE
              </Typography>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 700,
                  color: '#003399',
                  mb: 3,
                  mt: 1
                }}
              >
                Erasmus+ Project Generator
              </Typography>
              <Typography
                variant="body1"
                sx={{ color: '#555', lineHeight: 1.8, mb: 4, fontSize: '1.1rem' }}
              >
                We operate as Erasmus+ Project Generators, focusing on the strategic phase before submission.
                Our AI-powered platform transforms your ideas into professionally structured project proposals.
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {[
                  { icon: <IdeaIcon />, text: 'Project idea generation' },
                  { icon: <TimelineIcon />, text: 'Concept structuring and alignment with Erasmus+ priorities' },
                  { icon: <PartnershipIcon />, text: 'Partnership building at European level' },
                  { icon: <VerifiedIcon />, text: 'Preparation of projects ready for official submission' }
                ].map((item, idx) => (
                  <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: 2,
                        bgcolor: '#003399',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        '& svg': { color: '#FFCC00', fontSize: 24 }
                      }}
                    >
                      {item.icon}
                    </Box>
                    <Typography sx={{ fontWeight: 500, color: '#333', fontSize: '1.05rem' }}>
                      {item.text}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              {/* Process diagram */}
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  borderRadius: 4,
                  border: '1px solid #e0e0e0',
                  bgcolor: 'white'
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#003399', mb: 4, textAlign: 'center' }}>
                  From Idea to Funding
                </Typography>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {[
                    { step: '01', label: 'IDEA', desc: 'Your project concept', color: '#003399' },
                    { step: '02', label: 'PROJECT', desc: 'AI-powered structuring', color: '#0052cc' },
                    { step: '03', label: 'FUNDING', desc: 'National Agency submission', color: '#FFCC00' },
                    { step: '04', label: 'IMPACT', desc: 'European-scale results', color: '#4CAF50' }
                  ].map((item, idx) => (
                    <Box key={idx}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 3,
                          p: 2,
                          bgcolor: idx < 2 ? 'rgba(0, 51, 153, 0.03)' : 'transparent',
                          borderRadius: 2,
                          border: idx < 2 ? '2px solid rgba(0, 51, 153, 0.1)' : '2px dashed #e0e0e0'
                        }}
                      >
                        <Box
                          sx={{
                            width: 48,
                            height: 48,
                            borderRadius: '50%',
                            bgcolor: item.color,
                            color: item.color === '#FFCC00' ? '#003399' : 'white',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: 800,
                            fontSize: '0.9rem',
                            flexShrink: 0
                          }}
                        >
                          {item.step}
                        </Box>
                        <Box>
                          <Typography sx={{ fontWeight: 700, color: '#003399' }}>
                            {item.label}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {item.desc}
                          </Typography>
                        </Box>
                      </Box>
                      {idx < 3 && (
                        <Box sx={{ ml: 5, height: 16, borderLeft: '2px solid #e0e0e0' }} />
                      )}
                    </Box>
                  ))}
                </Box>

                <Typography
                  variant="body2"
                  sx={{ 
                    mt: 3, 
                    p: 2, 
                    bgcolor: 'rgba(0, 51, 153, 0.05)', 
                    borderRadius: 2,
                    color: '#003399',
                    fontWeight: 500,
                    textAlign: 'center'
                  }}
                >
                  We generate the project. The National Agency evaluates it.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* AI Features Section */}
      <Box sx={{ py: { xs: 8, md: 10 }, bgcolor: 'white' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography
              variant="overline"
              sx={{ 
                color: '#FFCC00', 
                fontWeight: 700, 
                letterSpacing: 2,
                bgcolor: 'rgba(255, 204, 0, 0.1)',
                px: 2,
                py: 0.5,
                borderRadius: 1,
                display: 'inline-block',
                mb: 2
              }}
            >
              TECHNOLOGY
            </Typography>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 700,
                color: '#003399',
                mb: 3
              }}
            >
              AI-Powered Generation
            </Typography>
            <Typography
              variant="h6"
              sx={{ color: '#666', maxWidth: 700, mx: 'auto', fontWeight: 400 }}
            >
              Our intelligent platform leverages advanced AI to transform your ideas into 
              professionally structured Erasmus+ applications
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {[
              {
                icon: <AIIcon />,
                title: 'Intelligent Content Generation',
                desc: 'AI creates comprehensive content for all application sections: objectives, methodologies, work packages, and more.',
                highlight: true
              },
              {
                icon: <SpeedIcon />,
                title: 'Minutes, Not Weeks',
                desc: 'Generate complete 25-30 page applications in minutes instead of weeks of manual work.'
              },
              {
                icon: <VerifiedIcon />,
                title: 'Professional Formatting',
                desc: 'Automatically formatted documents following EU/Erasmus+ standards and best practices.'
              },
              {
                icon: <PsychologyIcon />,
                title: 'Smart Suggestions',
                desc: 'Get intelligent suggestions for objectives, work packages, budget allocation, and project structure.'
              },
              {
                icon: <CloudIcon />,
                title: '1M+ Project Templates',
                desc: 'Access a vast database of project templates covering every sector, target group, and EU priority.'
              },
              {
                icon: <DocIcon />,
                title: 'Export Ready',
                desc: 'Download professional DOCX documents ready for copy-paste into official submission platforms.'
              }
            ].map((feature, idx) => (
              <Grid item xs={12} md={4} key={idx}>
                <Card
                  elevation={0}
                  sx={{
                    height: '100%',
                    p: 4,
                    border: feature.highlight ? '2px solid #FFCC00' : '1px solid #e0e0e0',
                    borderRadius: 3,
                    transition: 'all 0.3s',
                    position: 'relative',
                    overflow: 'hidden',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 16px 48px rgba(0, 51, 153, 0.12)'
                    }
                  }}
                >
                  {feature.highlight && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: 4,
                        bgcolor: '#FFCC00'
                      }}
                    />
                  )}
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: 2,
                      bgcolor: feature.highlight ? '#FFCC00' : 'rgba(0, 51, 153, 0.08)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 3,
                      '& svg': { 
                        fontSize: 28, 
                        color: feature.highlight ? '#003399' : '#003399' 
                      }
                    }}
                  >
                    {feature.icon}
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#003399', mb: 2 }}>
                    {feature.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#666', lineHeight: 1.7 }}>
                    {feature.desc}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* How Official Submission Works */}
      <Box 
        sx={{ 
          py: { xs: 8, md: 10 }, 
          background: 'linear-gradient(135deg, #003399 0%, #0052cc 100%)',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: 0.05,
            backgroundImage: `
              radial-gradient(circle at 10% 20%, white 2px, transparent 2px),
              radial-gradient(circle at 90% 80%, white 2px, transparent 2px),
              radial-gradient(circle at 50% 50%, white 3px, transparent 3px)
            `,
            backgroundSize: '100% 100%'
          }}
        />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 700,
                color: 'white',
                mb: 3
              }}
            >
              How Official Submission Works
            </Typography>
            <Typography
              variant="h6"
              sx={{ color: 'rgba(255, 255, 255, 0.85)', maxWidth: 700, mx: 'auto', fontWeight: 400 }}
            >
              Each participating country has its own Erasmus Plus National Agency, 
              evaluation procedures, and submission platforms
            </Typography>
          </Box>

          <Grid container spacing={4} justifyContent="center">
            <Grid item xs={12} md={5}>
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  bgcolor: 'rgba(255, 255, 255, 0.1)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: 3,
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  height: '100%'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: '50%',
                      bgcolor: '#FFCC00',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <AIIcon sx={{ color: '#003399', fontSize: 24 }} />
                  </Box>
                  <Typography variant="h6" sx={{ color: 'white', fontWeight: 700 }}>
                    We Generate
                  </Typography>
                </Box>
                <Typography sx={{ color: 'rgba(255,255,255,0.9)', lineHeight: 1.8 }}>
                  Our platform creates the complete project structure, content, objectives, 
                  work packages, and all required documentation. You receive a ready-to-submit proposal.
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} md={2} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <ArrowIcon sx={{ fontSize: 48, color: '#FFCC00', transform: isMobile ? 'rotate(90deg)' : 'none' }} />
            </Grid>

            <Grid item xs={12} md={5}>
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  bgcolor: 'rgba(255, 204, 0, 0.15)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: 3,
                  border: '2px solid #FFCC00',
                  height: '100%'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: '50%',
                      bgcolor: '#FFCC00',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <InstitutionIcon sx={{ color: '#003399', fontSize: 24 }} />
                  </Box>
                  <Typography variant="h6" sx={{ color: 'white', fontWeight: 700 }}>
                    National Agency Evaluates
                  </Typography>
                </Box>
                <Typography sx={{ color: 'rgba(255,255,255,0.9)', lineHeight: 1.8 }}>
                  Once generated and reviewed, you submit to your country's National Agency 
                  through their official platform. They evaluate and fund approved projects.
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          <Box sx={{ textAlign: 'center', mt: 6 }}>
            <Typography
              variant="h6"
              sx={{
                color: '#FFCC00',
                fontWeight: 600,
                p: 3,
                bgcolor: 'rgba(255, 204, 0, 0.1)',
                borderRadius: 2,
                display: 'inline-block'
              }}
            >
              This is how Erasmus Plus works.
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* How It Works (Steps) */}
      <Box sx={{ py: { xs: 8, md: 10 }, bgcolor: 'white' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography
              variant="overline"
              sx={{ 
                color: '#003399', 
                fontWeight: 700, 
                letterSpacing: 2
              }}
            >
              SIMPLE PROCESS
            </Typography>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 700,
                color: '#003399',
                mb: 3,
                mt: 1
              }}
            >
              How It Works
            </Typography>
          </Box>

          <Grid container spacing={0}>
            {[
              { step: 1, title: 'Enter Your Idea', desc: 'Start with your project concept - no commitment required' },
              { step: 2, title: 'Fill Project Details', desc: 'Complete basic information, content, and work packages' },
              { step: 3, title: 'Generate & Review', desc: 'AI generates your complete application document' },
              { step: 4, title: 'Export & Submit', desc: 'Download your professional document ready for submission' }
            ].map((item, idx) => (
              <Grid item xs={12} md={3} key={idx}>
                <Box sx={{ textAlign: 'center', position: 'relative' }}>
                  {idx < 3 && !isMobile && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 40,
                        right: -20,
                        width: 40,
                        height: 2,
                        bgcolor: '#FFCC00',
                        zIndex: 1
                      }}
                    />
                  )}
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      bgcolor: '#003399',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '2rem',
                      fontWeight: 800,
                      mx: 'auto',
                      mb: 3,
                      position: 'relative',
                      zIndex: 2
                    }}
                  >
                    {item.step}
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#003399', mb: 1 }}>
                    {item.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ px: 2 }}>
                    {item.desc}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box 
        sx={{ 
          py: { xs: 8, md: 10 }, 
          background: 'linear-gradient(135deg, #001a4d 0%, #003399 100%)'
        }}
      >
        <Container maxWidth="md">
          <Paper
            elevation={0}
            sx={{
              p: { xs: 4, md: 6 },
              textAlign: 'center',
              background: 'white',
              borderRadius: 4
            }}
          >
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: '#003399',
                mb: 2
              }}
            >
              Ready to Create Your Erasmus+ Project?
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: '#666', mb: 4, maxWidth: 500, mx: 'auto' }}
            >
              Start generating your project proposal today. No registration required to explore.
              Try it now and see what AI can do for your Erasmus+ application.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                size="large"
                endIcon={<ArrowIcon />}
                onClick={onStartProject}
                sx={{
                  bgcolor: '#003399',
                  fontWeight: 700,
                  px: 5,
                  py: 1.5,
                  fontSize: '1.1rem',
                  '&:hover': { bgcolor: '#002266' }
                }}
              >
                Start Your Project
              </Button>
              <Button
                variant="outlined"
                size="large"
                startIcon={<LoginIcon />}
                onClick={onShowLogin}
                sx={{
                  borderColor: '#003399',
                  color: '#003399',
                  fontWeight: 600,
                  px: 4,
                  py: 1.5,
                  '&:hover': { bgcolor: 'rgba(0, 51, 153, 0.05)' }
                }}
              >
                Sign In
              </Button>
            </Box>
          </Paper>
        </Container>
      </Box>

      {/* Footer */}
      <Box sx={{ py: 6, bgcolor: '#001a4d' }}>
        <Container maxWidth="lg">
          {/* Logo centrato */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Box
              component="img"
              src="/logo.png"
              alt="Erasmus+ Generator"
              sx={{ height: 50, objectFit: 'contain' }}
            />
          </Box>
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)' }}>
              © {new Date().getFullYear()} Erasmus+ Project Generator. All rights reserved.
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <Box
                component="a"
                href="https://www.linkedin.com/in/erasmus-project-generator-2119283a5/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
                sx={{
                  display: 'inline-flex',
                  color: 'rgba(255,255,255,0.7)',
                  '&:hover': { color: 'white' }
                }}
              >
                <LinkedInIcon sx={{ fontSize: 28 }} />
              </Box>
              {onShowContact && (
                <Typography 
                  variant="body2" 
                  onClick={onShowContact}
                  sx={{ 
                    color: 'rgba(255,255,255,0.7)', 
                    cursor: 'pointer',
                    '&:hover': { color: 'white' }
                  }}
                >
                  Contact Us
                </Typography>
              )}
              <Typography
                component="a"
                href="?page=privacy-policy"
                variant="body2"
                onClick={(e) => {
                  e.preventDefault();
                  setShowPrivacy(true);
                }}
                sx={{
                  color: 'rgba(255,255,255,0.7)',
                  cursor: 'pointer',
                  textDecoration: 'none',
                  '&:hover': { color: 'white' }
                }}
              >
                Privacy Policy
              </Typography>
              <Typography
                component="a"
                href="?page=cookie-policy"
                variant="body2"
                onClick={(e) => {
                  e.preventDefault();
                  setShowCookiePolicy(true);
                }}
                sx={{
                  color: 'rgba(255,255,255,0.7)',
                  cursor: 'pointer',
                  textDecoration: 'none',
                  '&:hover': { color: 'white' }
                }}
              >
                Cookie Policy
              </Typography>
              <Typography 
                variant="body2" 
                onClick={() => setShowTerms(true)}
                sx={{ 
                  color: 'rgba(255,255,255,0.7)', 
                  cursor: 'pointer',
                  '&:hover': { color: 'white' }
                }}
              >
                Terms of Service
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>
      <CookieBanner />
    </Box>
  );
};

export default LandingPage;
